import PropTypes from 'prop-types'
import React from 'react'

import classNames from 'classnames'
import escapeRegExp from 'lodash/escapeRegExp'
import identity from 'lodash/identity'

import WordlistShareInfo from './WordlistShareInfo'
import WordlistShareBox from './WordlistShareBox'

const HighlightWords = ({ words, string }) => {
  words = words.filter(identity)
  if (words.length === 0) {
    return <span>{string}</span>
  }
  let matches = string.match(
    new RegExp(
      `^(.*?)(${escapeRegExp(words[0])})(.*)$`,
      'i',
    ),
  )
  if (!matches) {
    return <span>{string}</span>
  }
  return (
    <span>
      {matches[1]}
      <span className="bg-silver-dark">{matches[2]}</span>
      {matches[3]}
    </span>
  )
}

HighlightWords.propTypes = {
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
  string: PropTypes.string.isRequired,
}

const WordlistTable = ({
  selectableEntries = false,
  languages,
  availableLanguages,
  defaultLanguageId,
  entries,
  totalEntries,
  selectedEntries = [],
  searchQuery = '',
  onSearchQueryInputChange,
  onShowLanguagePickerButtonClick,
  onLanguageCheckboxChange,
  onLoadMoreButtonClick,
  onEntrySpeakClick,
  onEntryCheckboxChange,
  onEditButtonClick,
  onDeleteButtonClick,
  onPrintButtonClick,
  onShareButtonClick,
  showEditButton = false,
  showDeleteButton = false,
  showPrintButton = false,
  showShareButton = false,
  showShareUrl = false,
  showLanguagePicker = false,
  userIsLoggedIn,
  isSharedList,
  authorName,
  duplicateList,
}) => {
  function disableContextMenu(e) {
    e.preventDefault()
    return false
  }
  return (
    <div>
      {isSharedList ? (
        <WordlistShareInfo
          authorName={authorName}
          enableCopy={userIsLoggedIn}
          onCopyClick={duplicateList}
        />
      ) : null}
      <div className="flex space-x-2 flex-wrap mb-6">
        <div className="flex items-center mr-2 flex-1">
          <span className="flex-none pr-2">Sök:</span>
          <input
            type="text"
            value={searchQuery}
            onChange={onSearchQueryInputChange}
            className="pl-2 font-medium bg-white border text-pv-green-dark border-pv-green-dark w-full h-full"
          />
        </div>
        <button
          className="inline-flex items-center self-stretch btn-white mt0! mr-2! mb-2! ml-auto!"
          onClick={onShowLanguagePickerButtonClick}
        >
          {`Välj språk`}
          <svg xmlns="http://www.w3.org/2000/svg" 
            className={`ml-2 h-4 w-4 transform ${
                showLanguagePicker
                  ? 'rotate-180'
                  : 'icon-caret-down'
              }`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>
        {showPrintButton ? (
          <button
            className="flex-none self-stretch btn-white mt0! mr-2! mb-2! ml-auto! js-print-button"
            onClick={onPrintButtonClick}
          >
            {`Skriv ut`}
          </button>
        ) : null}
        {showShareButton ? (
          <button
            className="inline-flex items-center self-stretch btn-white mt0! mr-2! mb-2! ml-auto!"
            onClick={onShareButtonClick}
          >
            {`Dela`}
            <svg xmlns="http://www.w3.org/2000/svg" 
            className={`ml-2 h-4 w-4 transform ${
                showShareUrl
                  ? 'rotate-180'
                  : 'icon-caret-down'
              }`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : null}
        {showEditButton ? (
          <button
            className="flex-none self-stretch btn-white mt0! mr-2! mb-2! ml-auto!"
            onClick={onEditButtonClick}
          >
            {`Redigera`}
          </button>
        ) : null}
        {showDeleteButton ? (
          <button
            className="flex-none self-stretch btn-white mt0! mr-2! mb-2! ml-auto!"
            onClick={onDeleteButtonClick}
          >
            {`Radera`}
          </button>
        ) : null}
      </div>
      {showShareUrl ? <WordlistShareBox /> : null}
      {showLanguagePicker ? (
        <div className="bg-gray-50 text-sm px-2 pt-4 pb-2 my-4">
          <ul className="list-reset" style={{ columnWidth: '200px' }}>
            {availableLanguages.map(language => {
              return (
                <li
                  key={language.id}
                  className="inline-flex w-full px-4 pb-4 items-baseline"
                >
                  <span className="flex-none">
                    <input
                      id={`wordlist-language-checkbox-${
                        language.id
                      }`}
                      type="checkbox"
                      checked={languages.some(
                        lang2 => lang2.id === language.id,
                      )}
                      disabled={
                        language.id === defaultLanguageId
                      }
                      onChange={onLanguageCheckboxChange}
                      value={language.id}
                    />
                  </span>
                  <label
                    htmlFor={`wordlist-language-checkbox-${
                      language.id
                    }`}
                    className="flex-auto pl-2 inline regular cursor-pointer"
                  >
                    {language.label}
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
      <div
        className="overflow-x-auto"
        data-snap-ignore="true"
      >
        <table
          className="table-fixed mt-2 w-full"
          style={{
            minWidth: `${languages.length * 8}rem`,
          }}
        >
          <colgroup>
            {selectableEntries ? (
              <col style={{ width: '1rem' }} />
            ) : null}
            {languages.map(language => {
              return (
                <col
                  key={language.id}
                  width={`${100 / languages.length}%`}
                />
              )
            })}
          </colgroup>
          <thead>
            <tr className="text-left text-gray-800">
              {selectableEntries ? <th /> : null}
              {languages.map(language => {
                return (
                  <th key={language.id}>
                    {language.label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className='text-sm text-gray-800 leading-tight'>
            {entries.map(entry => {
              return (
                <tr key={entry.id}>
                  {selectableEntries ? (
                    <td className="border-b border-gray-100">
                      <input
                        type="checkbox"
                        checked={selectedEntries.includes(
                          entry.id,
                        )}
                        onChange={onEntryCheckboxChange}
                        value={entry.id}
                      />
                    </td>
                  ) : null}
                  {languages.map(language => {
                    let value =
                      (entry.values[language.id] &&
                        entry.values[language.id].value) ||
                      ''
                    return (
                      <td
                        key={language.id}
                        className="border-b border-gray-100"
                      >
                        <div
                          className={classNames([
                            'p-2',
                            value &&
                              language.canSpeak &&
                              'silver-darker hover-green cursor-pointer',
                          ])}
                          onClick={
                            value &&
                            language.canSpeak &&
                            onEntrySpeakClick.bind(
                              null,
                              entry,
                              language,
                            )
                          }
                        >
                          <span className="black">
                            { language.code.indexOf('zxx-image') !== -1 && value ? (
                              <img
                                alt=""
                                src={value.replace('https://pedagogvarmland.se/sites/default/files/skolord/', wpApiSettings.wp_theme_url + '/static/images/skolord/')}
                                onContextMenu={
                                  disableContextMenu
                                }
                                style={{
                                  maxHeight: language.code.indexOf('zxx-image-') !== -1 ? language.code.substring(10)+'px' : '50px',
                                }}
                              />
                            ) : language.id ===
                            defaultLanguageId ? (
                              <HighlightWords
                                string={value}
                                words={[searchQuery]}
                              />
                            ) : (
                              value
                            )}
                          </span>
                          {value && language.canSpeak ? (
                            <span>
                              {' '}
                              <svg style={{height: "1rem", width: "1rem", color: "#747474", display: "inline"}} xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                              </svg>
                            </span>
                          ) : null}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="my2 muted center">
        {entries.length < totalEntries
          ? `Visar ${entries.length} av ${totalEntries}`
          : `Visar alla ${entries.length}`}
      </div>
      {entries.length < totalEntries ? (
        <div className="my2 flex justify-center">
          <button
            className="btn-white"
            onClick={onLoadMoreButtonClick}
          >
            {`Hämta fler`}
          </button>
        </div>
      ) : null}
    </div>
  )
}

WordlistTable.propTypes = {
  selectableEntries: PropTypes.bool,
  availableLanguages: PropTypes.array.isRequired,
  languages: PropTypes.array.isRequired,
  defaultLanguageId: PropTypes.string.isRequired,
  entries: PropTypes.array.isRequired,
  selectedEntries: PropTypes.arrayOf(PropTypes.string),
  totalEntries: PropTypes.number.isRequired,
  searchQuery: PropTypes.string,
  onSearchQueryInputChange: PropTypes.func,
  onShareButtonClick: PropTypes.func,
  onCopyUrlButtonClick: PropTypes.func,
  onShowLanguagePickerButtonClick: PropTypes.func,
  onLanguageCheckboxChange: PropTypes.func,
  onLoadMoreButtonClick: PropTypes.func,
  onEntrySpeakClick: PropTypes.func,
  onEntryCheckboxChange: PropTypes.func,
  onEditButtonClick: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
  onPrintButtonClick: PropTypes.func,
  showEditButton: PropTypes.bool,
  showDeleteButton: PropTypes.bool,
  showPrintButton: PropTypes.bool,
  showShareButton: PropTypes.bool,
  showShareUrl: PropTypes.bool,
  showLanguagePicker: PropTypes.bool,
}

export default WordlistTable
